import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listCompetitors, createCompetitor } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { EntityLogo } from "@/components/EntityLogo";
import { CompetitorModal } from "@/components/CompetitorModal";

export function CompetitionPage() {
  const { data: competitors, isLoading } = useQuery({
    queryKey: ["competitors"],
    queryFn: listCompetitors,
  });
  const [addOpen, setAddOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div>
      <header className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="page-title">Competition</h1>
          <p className="mt-2 text-text-secondary">
            Profiles of the companies we compete with — what they do, plus pictures and files.
          </p>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
          style={{ color: "var(--color-on-accent)" }}
        >
          <i className="fa-solid fa-plus mr-1.5 text-xs" />
          Add competitor
        </button>
      </header>

      {isLoading ? (
        <div className="grid place-items-center py-16 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-lg" />
        </div>
      ) : !competitors || competitors.length === 0 ? (
        <EmptyState onAdd={() => setAddOpen(true)} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {competitors.map((c) => (
            <button
              key={c.id}
              onClick={() => setOpenId(c.id)}
              className="flex flex-col rounded-token-lg border border-border bg-bg-card p-4 text-left transition-colors hover:bg-bg-card-hover"
            >
              <div className="mb-3 flex items-center gap-3">
                <EntityLogo name={c.name} url={c.logoUrl} size={40} fallbackIcon="fa-chess-knight" />
                <div className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-text-heading">{c.name}</span>
                  {c.website && (
                    <span className="block truncate text-xs text-text-muted">
                      {c.website.replace(/^https?:\/\//, "")}
                    </span>
                  )}
                </div>
              </div>
              <p className="line-clamp-3 min-h-[3rem] text-sm text-text-secondary">
                {c.description || <span className="text-text-muted">No description yet.</span>}
              </p>
              <div className="mt-3 border-t border-border-light pt-2.5 text-xs text-text-muted">
                <i className="fa-solid fa-paperclip mr-1.5" />
                {c.fileCount} {c.fileCount === 1 ? "file" : "files"}
              </div>
            </button>
          ))}
        </div>
      )}

      {addOpen && (
        <NewCompetitorModal onClose={() => setAddOpen(false)} onCreated={setOpenId} />
      )}
      {openId && <CompetitorModal competitorId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-center">
      <div className="max-w-sm">
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-token-lg bg-accent-muted text-2xl text-accent">
          <i className="fa-solid fa-chess-knight" />
        </div>
        <h2 className="font-display text-lg font-semibold text-text-heading">
          No competitors yet
        </h2>
        <p className="mx-auto mt-1.5 text-sm text-text-secondary">
          Add your first competitor — describe what they do and attach pictures or files.
        </p>
        <button
          onClick={onAdd}
          className="mt-4 rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
          style={{ color: "var(--color-on-accent)" }}
        >
          <i className="fa-solid fa-plus mr-1.5 text-xs" />
          Add competitor
        </button>
      </div>
    </div>
  );
}

function NewCompetitorModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");

  const create = useMutation({
    mutationFn: () =>
      createCompetitor({
        name,
        website: website || undefined,
        description: description || undefined,
      }),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["competitors"] });
      onClose();
      onCreated(c.id);
    },
  });

  const inputCls =
    "mb-3 w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";

  return (
    <Modal open onClose={onClose} title="Add competitor">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <label className="mb-1 block text-xs font-medium text-text-muted">Name</label>
        <input
          className={inputCls}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          required
        />

        <label className="mb-1 block text-xs font-medium text-text-muted">Website</label>
        <input
          className={inputCls}
          value={website}
          placeholder="https://…"
          onChange={(e) => setWebsite(e.target.value)}
        />

        <label className="mb-1 block text-xs font-medium text-text-muted">
          What they do
        </label>
        <textarea
          className={inputCls}
          rows={4}
          placeholder="Their product, positioning, strengths and weaknesses…"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-token-md border border-border px-4 py-2 text-sm text-text-secondary hover:text-text-heading"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ color: "var(--color-on-accent)" }}
          >
            {create.isPending ? "Adding…" : "Add competitor"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
